import { SafePromise, safeMap } from '@benzinga/safe-await';
import { RestfulClient } from '@benzinga/session';
import { Session } from '@benzinga/session';
import { Layout, LayoutList } from './entities';
import { Operation } from 'fast-json-patch';
import { LayoutEnvironment } from './environment';

export class LayoutRestful extends RestfulClient {
  constructor(session: Session) {
    super(session.getEnvironment(LayoutEnvironment).url, session, { 'x-device-key': true });
  }

  public getLayouts = (): SafePromise<Layout[]> => {
    const url = this.URL('api/v1/layouts/', {
      max_layout_version: this.session.getEnvironment(LayoutEnvironment).maxLayoutVersion,
    });
    return this.get(url, {
      // includeHeader: { 'x-session-key': true },
      resilience: {
        delayMultiple: 5000,
        delayOffset: 1000,
        maxNumOfAttempts: 5,
      },
    });
  };

  public getLayoutsList = (): SafePromise<LayoutList> => {
    const url = this.URL('api/v1/layouts/list/', {
      max_layout_version: this.session.getEnvironment(LayoutEnvironment).maxLayoutVersion,
    });
    return this.get(url, {
      // includeHeader: { 'x-session-key': true },
      resilience: {
        delayMultiple: 5000,
        delayOffset: 5000,
        maxNumOfAttempts: 5,
      },
    });
  };

  public getLayout = (uuid: string): SafePromise<Layout> => {
    const url = this.URL(`api/v1/layouts/${uuid}/`);
    return this.get(url, {
      // includeHeader: { 'x-session-key': true },
      resilience: {
        delayMultiple: 5000,
        delayOffset: 5000,
        maxNumOfAttempts: 5,
      },
    });
  };

  public beaconLayout = (uuid: Layout['uuid'], layout: Layout['data']): boolean => {
    const url = this.URL(`api/v1/layouts/${uuid}/`);
    return this.sendBeacon(url, layout);
  };

  public patchLayout = (uuid: Layout['uuid'], operations: Operation[]): SafePromise<undefined> => {
    const url = this.URL(`api/v1/layouts/${uuid}/`);
    return this.patch(url, operations, {
      includeHeader: { 'x-Layout-Response': 'minimal', 'x-session-key': true },
      resilience: {
        delayMultiple: 5000,
        delayOffset: 3000,
        maxNumOfAttempts: 5,
      },
    });
  };

  public postLayout = (name?: string, layout?: Layout['data']): SafePromise<Layout> => {
    const url = this.URL(`api/v1/layouts/`);
    return this.post(
      url,
      {
        ...(name && { name }),
        data: layout ?? {},
      },
      {
        includeHeader: { 'x-session-key': true },
        resilience: {
          delayMultiple: 3000,
          delayOffset: 3000,
          maxNumOfAttempts: 5,
        },
      },
    );
  };

  public putLayout = (uuid: Layout['uuid'], layout: Layout['data']): SafePromise<Layout> => {
    const url = this.URL(`api/v1/layouts/${uuid}/`);
    return this.put(url, layout, {
      includeHeader: { 'x-session-key': true },
      resilience: {
        delayMultiple: 3000,
        delayOffset: 1000,
        maxNumOfAttempts: 5,
      },
    });
  };

  public deleteLayout = (uuid: Layout['uuid']): SafePromise<undefined> => {
    const url = this.URL(`api/v1/layouts/${uuid}/`);
    return safeMap(
      this.delete(url, {
        includeHeader: { 'x-session-key': true },
        resilience: {
          delayMultiple: 1000,
          delayOffset: 1000,
          maxNumOfAttempts: 5,
        },
      }),
      () => undefined,
    );
  };
}
